import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    useMediaQuery,
    TextField,
    Alert
} from '@mui/material';
import { styled, keyframes, useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
// import AppleIcon from '@mui/icons-material/Apple';
// import AndroidIcon from '@mui/icons-material/Android';
import CategoryCarousel from '../components/CategoryCarousel';

import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';


/* ---------- Animations ---------- */
const float = keyframes`
  0%   { transform: translateY(0)   }
  50%  { transform: translateY(-20px) }
  100% { transform: translateY(0)   }
`;

const gradient = keyframes`
  0%   { background-position: 0% 50% }
  50%  { background-position: 100% 50% }
  100% { background-position: 0% 50% }
`;

const slideIn = keyframes`
  from { transform: translateY(100%); opacity: 0 }
  to   { transform: translateY(0);   opacity: 1 }
`;

const AppDownloadButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#FFFFFF',
    margin: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    },
}));

/* ---------- Styled components ---------- */
const HeroSection = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    background: "#33a853",
    backgroundColor: theme.palette.primary.main,
    backgroundSize: '400% 400%',
    animation: `${gradient} 15s ease infinite`,
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.05)',
        zIndex: 1
    },
    [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    }
}));
const HeroContent = styled(Container)(({ theme }) => ({
    position: 'relative',
    zIndex: 2,
    color: '#fff',
    [theme.breakpoints.down('sm')]: {
        textAlign: 'center'
    }
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1),
    borderRadius: 50,
    display: 'flex',
    alignItems: 'center',
    maxWidth: 600,
    margin: '0 auto',
    marginTop: theme.spacing(4),
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    transition: 'all 0.3s ease',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 15px 50px rgba(0,0,0,0.3)'
    },
    '&:focus-within': {
        transform: 'translateY(-2px)',
        boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
        backgroundColor: 'rgba(255, 255, 255, 1)'
    }
}));

const SearchInput = styled(TextField)(({ theme }) => ({
    flex: 1,
    '& .MuiOutlinedInput-root': {
        border: 'none',
        '& fieldset': { border: 'none' },
        '&:hover fieldset': { border: 'none' },
        '&.Mui-focused fieldset': { border: 'none' }
    },
    '& .MuiInputBase-input': {
        padding: theme.spacing(1.5, 2),
        fontSize: '1.1rem',
        '&::placeholder': { color: theme.palette.text.secondary, opacity: 0.7 }
    }
}));

const SearchButton = styled(Button)(({ theme }) => ({
    borderRadius: 40,
    padding: theme.spacing(1.5, 3),
    minWidth: 'auto',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
    '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
    }
}));

const CategoryIcon = styled('img')(() => ({
    width: 60,
    height: 60,
    borderRadius: '50%',
    backgroundColor: '#fff',
    padding: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
        transform: 'scale(1.1) rotate(5deg)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
    }
}));

const FloatingCategory = styled(Box)(({ delay = 0 }) => ({
    position: 'absolute',
    animation: `${float} ${3 + delay}s ease-in-out infinite`,
    animationDelay: `${delay}s`
}));

const FeatureCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: 20,
    background: theme.palette.background.paper,
    overflow: 'visible',
    transition: 'all 0.3s ease',
    position: 'relative',
    '&:hover': {
        transform: 'translateY(-10px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        '& .feature-icon': { transform: 'rotate(360deg) scale(1.2)' }
    }
}));

/* --- FIXED: testimonial quote pseudo was breaking small screens --- */
const TestimonialCard = styled(Card)(({ theme }) => ({
    borderRadius: 20,
    padding: theme.spacing(4),
    background: '#fff',
    position: 'relative',
    overflow: 'visible',
    '&::before': {
        content: '"\\201C"',          // decorative opening quote
        position: 'absolute',
        top: -16,
        left: 16,
        fontSize: 80,
        color: theme.palette.primary.main,
        opacity: 0.1
    },
    [theme.breakpoints.down('sm')]: {
        '&::before': { display: 'none' } // hide on very small screens
    }
}));

const ActionButton = styled(Button)({
    borderRadius: 30,
    padding: '12px 40px',
    fontWeight: 600,
    textTransform: 'none',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 0,
        height: 0,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.3)',
        transform: 'translate(-50%, -50%)',
        transition: 'width 0.6s, height 0.6s'
    },
    '&:hover::before': { width: 300, height: 300 }
});

/* --- MOBILE-APP PROMPT --- */
const MobileAppPrompt = styled(Alert)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: theme.zIndex.snackbar,
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    animation: `${slideIn} 0.5s ease-out`,
    backdropFilter: 'blur(10px)',
    backgroundColor: '#fff',
    color: theme.palette.success.main,
    border: `2px solid ${theme.palette.success.main}`,
    '&&': {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        border: `2px solid ${theme.palette.primary.dark}`,
        backdropFilter: 'none',     // if you don’t want any blur
    },

}));


/* ========================================================================= */

export default function HomePage() {
    const theme = useTheme();
    const navigate = useNavigate();

    /* breakpoints */
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /* state */
    const [showMobilePrompt, setShowMobilePrompt] = useState(false);
    const [topCategories, setTopCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const featuresRef = useRef(null);

    /* ---------- Fetch categories -------- */
    useEffect(() => {
        fetch('/category_data/categories.json')
            .then(res => res.json())
            .then(data => {
                // only available categories, then shuffle in-place
                const availableCats = data.filter(c => c.available);
                for (let i = availableCats.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [availableCats[i], availableCats[j]] = [availableCats[j], availableCats[i]];
                }
                setTopCategories(availableCats);
            })
            .catch(err => console.error('Failed to load categories', err));
    }, []);

    /* ---------- Show the mobile-app prompt immediately -------- */
    useEffect(() => {
        if (isMobile) {
            setShowMobilePrompt(true);
        }
    }, [isMobile]);

    /* ---------- Feature cards fade-in -------- */
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach((entry, idx) => {
                    if (entry.isIntersecting) {
                        entry.target.style.opacity = 1;
                        entry.target.style.transform = 'translateY(0)';
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );
        if (featuresRef.current) {
            featuresRef.current
                .querySelectorAll('.feature-card')
                .forEach(el => {
                    el.style.opacity = 0;
                    el.style.transform = 'translateY(40px)';
                    observer.observe(el);
                });
        }
        return () => observer.disconnect();
    }, []);

    /* ---------- Handlers -------- */
    const handleSearch = e => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/listings?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const handleAppDownload = platform => {
        const storeUrls = {
            ios: 'https://apps.apple.com/us/app/getborrowed/id6741023320',
            android: 'https://play.google.com/store/apps/details?id=com.getborrowed.app'
        };
        window.open(storeUrls[platform], '_blank');
        handleDismissPrompt();
    };

    const handleDismissPrompt = () => {
        setShowMobilePrompt(false);
        localStorage.setItem('mobileAppPromptDismissed', 'true');
    };

    /* ---------- Floating category placement -------- */
    const floatingPositions = [
        { top: '10%', left: '5%' },
        { top: '20%', right: '10%' },
        { top: '60%', left: '8%' },
        { top: '70%', right: '5%' },
        { top: '40%', left: '3%' },
        { top: '80%', right: '15%' }
    ];

    const bgCategories = topCategories.slice(0, floatingPositions.length).map((cat, i) => ({
        icon: cat.logo_url || `/icons/${cat.id}.png`,
        name: cat.name,
        position: floatingPositions[i]
    }));

    /* ---------- Feature and testimonial data -------- */
    const features = [
        {
            icon: <LocalAtmIcon className="feature-icon" sx={{ fontSize: 60, transition: 'all 0.5s' }} />,
            title: 'Save Money',
            description: 'Why buy when you can borrow? Only pay for what you need, when you need it.',
            color: theme.palette.primary.main
        },
        {
            icon: <AccessTimeIcon className="feature-icon" sx={{ fontSize: 60, transition: 'all 0.5s' }} />,
            title: 'Quick & Easy',
            description: 'List or find items in seconds with our intuitive interface and smart search.',
            color: theme.palette.primary.main
        },
        {
            icon: <VerifiedUserIcon className="feature-icon" sx={{ fontSize: 60, transition: 'all 0.5s' }} />,
            title: 'Trusted Community',
            description: 'Verified profiles and transparent reviews give you confidence every time.',
            color: theme.palette.primary.main
        }
    ];

    const testimonials = [
        { name: 'Emma S.', rating: 5, text: 'The platform is so intuitive—I found a camera lens within minutes!' },
        { name: 'Mike R.', rating: 5, text: 'Listing my tools was effortless and I earned extra income right away.' },
        { name: 'Lisa T.', rating: 5, text: 'I love borrowing garden equipment without cluttering my garage.' }
    ];

    const LogoImage = styled('img')(({ theme }) => ({
        width: 252,   // was 126
        height: 60,   // was 30
        objectFit: 'contain',
    }));

    /* ========================================================================= */
    return (
        <Box sx={{ overflow: 'hidden' }}>
            {/* ---------- HERO ---------- */}
            <HeroSection>
                {!isMobile &&
                    bgCategories.map((cat, i) => (
                        <FloatingCategory key={i} sx={cat.position} delay={i * 0.5}>
                            <CategoryIcon src={cat.icon} alt={cat.name} />
                        </FloatingCategory>
                    ))}

                <HeroContent maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Box
                                component={RouterLink}
                                to="/"
                            // sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexGrow: 1 }}
                            >
                                <LogoImage src={`${process.env.PUBLIC_URL}/logo_with_name_white_all.png`} alt="Borrowed Logo" />
                            </Box>

                            {/* <AnimatedTitle variant="h1">
                                Share More,<br />Own Less
                            </AnimatedTitle> */}
                            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                                Borrow what you need. Lend what you don’t use. <br></br> Why buy when you can Borrow!
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <ActionButton
                                    variant="contained"
                                    size="large"
                                    component={RouterLink}
                                    to="/login"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{ bgcolor: '#fff', color: theme.palette.primary.main }}
                                >
                                    Get Started
                                </ActionButton>
                                <ActionButton
                                    variant="outlined"
                                    size="large"
                                    component={RouterLink}
                                    to="/how-it-works"
                                    sx={{ borderColor: '#fff', color: '#fff' }}
                                >
                                    How It Works
                                </ActionButton>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <form onSubmit={handleSearch}>
                                <SearchContainer elevation={0}>
                                    <SearchIcon sx={{ ml: 2, color: 'text.secondary' }} />
                                    <SearchInput
                                        placeholder="Try 'drill' or 'camera lens'..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        variant="outlined"
                                    />
                                    <SearchButton variant="contained" type="submit" disabled={!searchQuery.trim()}>
                                        Search
                                    </SearchButton>
                                </SearchContainer>
                            </form>
                        </Grid>
                    </Grid>
                </HeroContent>
            </HeroSection>

            {/* ---------- FEATURES ---------- */}
            <Box sx={{ py: 10, bgcolor: theme.palette.background.default }} ref={featuresRef}>
                <Container maxWidth="lg">
                    <Typography variant="h2" align="center" gutterBottom sx={{ fontWeight: 700, mb: 8 }}>
                        Why Borrowed?
                    </Typography>
                    <Grid container spacing={4}>
                        {features.map((feat, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <FeatureCard className="feature-card" elevation={0}>
                                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                        <Box
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                mb: 3,
                                                mx: 'auto',
                                                borderRadius: '50%',
                                                bgcolor: `${feat.color}20`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: feat.color
                                            }}
                                        >
                                            {feat.icon}
                                        </Box>
                                        <Typography variant="h5" fontWeight={700} gutterBottom>
                                            {feat.title}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {feat.description}
                                        </Typography>
                                    </CardContent>
                                </FeatureCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ---------- POPULAR CATEGORIES ---------- */}
            <Box sx={{ py: 8, bgcolor: theme.palette.background.paper }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" align="center" gutterBottom sx={{ fontWeight: 700, mb: 6 }}>
                        Find What You Need
                    </Typography>
                    <CategoryCarousel categories={topCategories} />
                </Container>
            </Box>

            {/* ---------- TESTIMONIALS ---------- */}
            <Box sx={{ py: 10, bgcolor: theme.palette.background.default }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" align="center" gutterBottom sx={{ fontWeight: 700, mb: 8 }}>
                        What People Are Saying
                    </Typography>
                    <Grid container spacing={4}>
                        {testimonials.map((t, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <TestimonialCard elevation={0}>
                                    <Box sx={{ display: 'flex', mb: 2 }}>
                                        {Array.from({ length: t.rating }).map((_, idx) => (
                                            <StarIcon key={idx} sx={{ color: theme.palette.primary.main }} />
                                        ))}
                                    </Box>
                                    <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
                                        "{t.text}"
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        – {t.name}
                                    </Typography>
                                </TestimonialCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ---------- MOBILE-APP PROMPT ---------- */}
            {showMobilePrompt && (
                <MobileAppPrompt
                    onClose={handleDismissPrompt}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AppDownloadButton onClick={() => handleAppDownload('ios')} title="Download for iOS">
                                <AppleIcon sx={{ fontSize: 28 }} />
                            </AppDownloadButton>
                            <AppDownloadButton onClick={() => handleAppDownload('android')} title="Download for Android">
                                <AndroidIcon sx={{ fontSize: 28 }} />
                            </AppDownloadButton>
                            {/* close‐icon */}
                            <IconButton
                                aria-label="close prompt"
                                onClick={handleDismissPrompt}
                                sx={{ color: 'white', ml: 1 }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    }
                >
                    <Typography variant="body2" sx={{ opacity: 0.9 }} fontWeight={600}>
                        Get the app for the best experience
                    </Typography>
                </MobileAppPrompt>
            )}
        </Box>
    );
}
